import { useCallback, useState } from 'react';

import axios from '@/lib/axios';
import { IBodyDocumentPermission } from '@/pages/Dialog/DialogShare';

import { useToast } from './use-toast';

export interface User {
  id: string;
  username: string;
  email?: string;
  groupname: string;
  role: string;
}

export interface Group {
  id: string;
  groupname: string;
  users: number;
}

export const enum DocumentPermission {
  'VIEW_DOCUMENT' = 'VIEW_DOCUMENT',
  'DOWNLOAD_DOCUMENT' = 'DOWNLOAD_DOCUMENT',
  'EDIT_DOCUMENT' = 'EDIT_DOCUMENT',
  'OWNER_DOCUMENT' = 'OWNER_DOCUMENT',
}

export const useSharingApi = (apiUrl: string, rootUrlHostApi: string) => {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [sharedUsers, setSharedUsers] = useState<IBodyDocumentPermission[]>([]);
  const [sharedGroups, setSharedGroups] = useState<IBodyDocumentPermission[]>([]);
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [loadingGrandted, setLoadingGrandted] = useState(false);

  const postGranted = useCallback(
    async (path: string, grandtedBy: string, data: IBodyDocumentPermission[]) => {
      if (loadingGrandted) return;

      setLoadingGrandted(true);
      try {
        const response = await axios.post(apiUrl + '/document-permission/grandted', {
          path,
          grandtedBy,
          data,
        });

        if (response.status === 200) {
          toast({
            title: 'Phân quyền tài liệu thành công.',
          });
          await fetchData(path);
        }

        return response;
      } catch (err) {
        return null;
      } finally {
        setLoadingGrandted(false);
      }
    },
    [],
  );

  const fetchAllUsers = useCallback(async () => {
    try {
      const res = await axios.get<User[]>(rootUrlHostApi + 'admin/users/get-all', {
        params: {
          limit: 10000,
        },
      });
      const users = res.data;
      const nonMasterAdminUsers = (users || [])?.filter((u) => u.role !== 'MASTER_ADMIN');
      return nonMasterAdminUsers;
    } catch (err) {
      console.error('Error fetchAllUsers', err);
      return [];
    }
  }, []);

  const fetchAllGroups = useCallback(async () => {
    try {
      const res = await axios.get<any>(rootUrlHostApi + 'admin/group-users');
      return res.data
        .filter((data: any) => `${data.groupname}`.toUpperCase() !== 'MASTER_ADMIN')
        .map((data: any) => ({ ...data, users: data.users?.length || 0 }));
    } catch (err) {
      console.error('Error fetchAllGroups', err);
      return [];
    }
  }, []);

  const fetchShared = useCallback(async (path: string) => {
    try {
      const res = await axios.get<any>(apiUrl + `/document-permission/grandted`, {
        params: {
          path,
        },
      });

      const data = res.data;
      return {
        usersShared: (data?.users as IBodyDocumentPermission[]) || [],
        groupsShared: (data?.groups as IBodyDocumentPermission[]) || [],
      };
    } catch (err) {
      console.error('Error fetchShared', err);
      return {
        usersShared: [],
        groupsShared: [],
      };
    }
  }, []);

  const fetchData = useCallback(async (prefix: string) => {
    if (loading) return;

    setLoading(true);

    let [users, groups, { usersShared, groupsShared }] = await Promise.all([
      fetchAllUsers(),
      fetchAllGroups(),
      fetchShared(prefix),
    ]);

    users = users?.filter(
      (urs) => !usersShared?.find((shared: IBodyDocumentPermission) => shared.userId === urs.id),
    );
    groups = groups?.filter(
      (gr: any) =>
        !groupsShared?.find((shared: IBodyDocumentPermission) => shared.groupname === gr.groupname),
    );

    Array.isArray(users) && setUsers(users);
    Array.isArray(groups) && setGroups(groups as any);
    Array.isArray(usersShared) && setSharedUsers(usersShared);
    Array.isArray(groupsShared) && setSharedGroups(groupsShared);

    setLoading(false);
  }, []);

  return {
    // Data
    users,
    groups,
    sharedUsers,
    sharedGroups,

    // Set
    setUsers,
    setGroups,
    setSharedUsers,
    setSharedGroups,

    fetchData,
    loading,

    // Grandted
    postGranted,
    loadingGrandted,
  };
};
